// PriceTypeFilterButton.js - Closes only when both selections are made

import { useEffect, useState } from "react";
import { Checkbox } from "reusableComponents";
import { useTranslation } from "react-i18next";
import { getPriceTypes } from "helpers/getPriceTypes";
import FiltersButton from "../FiltersButton";
import styles from "./styles.module.scss";

const PriceTypeFilterButton = ({
  selectedPriceType,
  onPriceSelect,
  minPrice,
  maxPrice,
  selectedUsage,
}) => {
  const [t] = useTranslation("global");
  const [showPriceDropdown, setShowPriceDropdown] = useState(true); // Start open by default
  const [priceOptions, setPriceOptions] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);

  useEffect(() => {
    const fetchedPriceOptions = getPriceTypes(selectedUsage);
    setPriceOptions(fetchedPriceOptions || []);
  }, [selectedUsage]);

  // Track selected price range for UI decisions
  useEffect(() => {
    if (minPrice !== null || maxPrice !== null) {
      setSelectedPriceRange({ min: minPrice, max: maxPrice });
    } else {
      setSelectedPriceRange(null);
    }
  }, [minPrice, maxPrice]);

  // Check if we have a complete selection (both type and range)
  const hasCompleteSelection = () => {
    return selectedPriceType && (minPrice !== null || maxPrice !== null);
  };

  const toggleShowPriceDropdown = () => {
    setShowPriceDropdown((prev) => !prev);
  };

  const handleSelectPriceOption = (option) => {
    const isSelected = option.type === selectedPriceType;
    
    if (isSelected) {
      // Deselecting price type - also clear any price range
      onPriceSelect(null, null, null);
      // Don't close dropdown when deselecting
    } else {
      // Selecting a new price type
      onPriceSelect(option.type, null, null);
      // Don't close dropdown yet - need to select price range too
    }
  };

  const handlePriceRangeChange = (priceRange) => {
    const isSelected = minPrice === priceRange.min && maxPrice === priceRange.max;
    
    if (isSelected) {
      // Deselecting price range
      onPriceSelect(selectedPriceType, null, null);
      // Don't close dropdown when deselecting
    } else {
      // Selecting a new price range
      onPriceSelect(selectedPriceType, priceRange.min, priceRange.max);
      
      // Close dropdown only if we have both selections (price type and price range)
      if (selectedPriceType) {
        setShowPriceDropdown(false);
      }
    }
  };

  // Get button label based on selection
  const getButtonLabel = () => {
    if (selectedPriceType) {
      const selectedOption = priceOptions.find(opt => opt.type === selectedPriceType);
      if (selectedOption) {
        // Check if we have a specific price range selected
        if (minPrice !== null || maxPrice !== null) {
          const priceRange = selectedOption.price?.find(p => 
            p.min === minPrice && p.max === maxPrice
          );
          if (priceRange) {
            return priceRange.max === null
              ? `${t(selectedOption.key)}: + ${priceRange.min} €`
              : `${t(selectedOption.key)}: ${t("search.bar.upTo")} ${priceRange.max} €`;
          }
        }
        return t(selectedOption.key);
      }
    }
    return t("search.price");
  };

  // Check if a price range is selected
  const isPriceRangeSelected = (priceRange) => {
    return minPrice === priceRange.min && maxPrice === priceRange.max;
  };

  return (
    <div className={styles.priceWrapper}>
      <FiltersButton
        label={getButtonLabel()}
        isActive={hasCompleteSelection()}
        onClick={toggleShowPriceDropdown}
      />
      <div
        className={`${styles.priceDropdownBody} ${
          showPriceDropdown ? styles.visible : styles.hidden
        }`}
      >
        {priceOptions.length > 0 ? (
          <div className={styles.priceOptionList}>
            {priceOptions.map((option) => (
              <div key={option.type} className={styles.priceOption}>
                <Checkbox
                  checked={selectedPriceType === option.type}
                  onChange={() => handleSelectPriceOption(option)}
                  label={t(option.key)}
                  greyText
                />
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noValuesText}>{t("search.priceMissingValues")}</p>
        )}

        {selectedPriceType && priceOptions.find((opt) => opt.type === selectedPriceType)?.price?.length > 0 && (
          <div className={styles.selectedPriceList}>
            {priceOptions
              .find((option) => option.type === selectedPriceType)
              ?.price?.map((priceRange, index) => (
                <div key={index} className={styles.priceOption}>
                  <Checkbox
                    checked={isPriceRangeSelected(priceRange)}
                    onChange={() => handlePriceRangeChange(priceRange)}
                    label={
                      priceRange.max === null
                        ? `+ ${priceRange.min} €`
                        : `${t("search.bar.upTo")} ${priceRange.max} €`
                    }
                    greyText
                  />
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceTypeFilterButton;